import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import {
  concat,
  defer,
  firstValueFrom,
  from,
  map,
  Observable,
  reduce
} from 'rxjs';
import {
  DOCUMENTS_FOR_UPLOAD_STORE_NAME,
  DocumentsForUploadRowData,
  DocumentsForUploadRowType
} from '../db-description';
import { ExcelGeneratorData } from '../dto/excel-generation-data.dto';
import { calculateDocumentChecksum } from '../utils/document-checksum.util';

const TIMESTAMP_7_DAYS: number = 7 * 24 * 60 * 60 * 1000;
const TIMESTAMP_30_DAYS: number = 30 * 24 * 60 * 60 * 1000;

/**
 * Очередь сгенерированных документов сделки (ТТН, ТРН, отчёт экспедитора) на выгрузку — часть офлайн-
 * контура. Документ формируется на устройстве, кладётся сюда в IndexedDB и позже выгружается на сервер;
 * до подтверждённой выгрузки он не теряется, даже при долгом офлайне. Отправленность помечается
 * контрольной суммой ({@code uploadedChecksum}): если документ изменили после выгрузки — он снова
 * считается неотправленным и переотправится.
 *
 * Все операции чтение-изменение-запись/удаление сериализуются через {@link #mutationChain} (см. ниже):
 * ngx-indexed-db открывает отдельную транзакцию на каждый вызов, и без сериализации две операции могли
 * бы затереть данные друг друга.
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentsStorageService {
  private readonly storeName: string = DOCUMENTS_FOR_UPLOAD_STORE_NAME;

  // Цепочка сериализации операций чтение-изменение-запись/удаления над хранилищем документов.
  // ngx-indexed-db открывает ОТДЕЛЬНУЮ транзакцию на каждый вызов, поэтому getByKey и следующий за ним
  // bulkPut/bulkDelete не атомарны: между ними могла бы вклиниться правка документа (load-car) или
  // отметка об отправке (documents-submit), затерев чужие данные и дав ложное «отправлено». Реальные
  // документы пишутся ТОЛЬКО через этот сервис, поэтому последовательное выполнение внутри него
  // закрывает окно гонки (тот же приём, что mutateTable в background-sync). Диагностическая запись
  // health-check идёт мимо цепочки, но по ключу-сентинелу, не пересекающемуся с реальными документами,
  // и без read-modify-write над чужими данными — на инвариант не влияет.
  private mutationChain: Promise<unknown> = Promise.resolve();

  constructor(private readonly dbService: NgxIndexedDBService) {}

  public getRecentDocs(): Observable<DocumentsForUploadRowData[]> {
    const user = this.fetchCurrentUser();
    const recentThreshold: number = Date.now() - TIMESTAMP_7_DAYS;
    // Чтение (без сериализации): актуальность отметки об отправке перепроверяется позже в
    // markUploadedIfUnchanged уже под сериализацией, поэтому устаревший снимок кандидатов безопасен.
    // Кандидаты на отправку: ВСЕ неотправленные документы пользователя (без uploadedChecksum) ЛЮБОГО
    // возраста — иначе документ, не ушедший на сервер за 7 дней офлайна, больше никогда не был бы
    // отправлен; плюс недавние (за 7 дней) для повторной проверки изменившихся данных.
    return this.dbService
      .getAllByIndex<DocumentsForUploadRowData>(
        this.storeName,
        'user',
        IDBKeyRange.only(user)
      )
      .pipe(
        map(rows =>
          rows.filter(
            row => !row.uploadedChecksum || row.updatedOn >= recentThreshold
          )
        )
      );
  }

  // Отчёт экспедитора относится к ЗАЯВКЕ целиком, а не к рейсу, поэтому в составном ключе номер дня
  // пуст (orderNumber ''), а идентификатор рейса — заглушка ('-1').
  public storeForwarderReport(
    docData: ExcelGeneratorData,
    params: {
      requestId: number;
    }
  ): Observable<IDBValidKey> {
    return this.storeDoc({
      forwardingRequestId: params.requestId,
      docType: DocumentsForUploadRowType.FORWARDER_REPORT,
      orderNumber: '',
      data: docData,
      vehicleDataId: '-1'
    });
  }

  public storeTrn(
    docData: ExcelGeneratorData,
    params: {
      requestId: number;
      orderNumber: string;
      vehicleDataId: string;
    }
  ): Observable<IDBValidKey> {
    return this.storeDoc({
      forwardingRequestId: params.requestId,
      docType: DocumentsForUploadRowType.TRN,
      orderNumber: params.orderNumber,
      vehicleDataId: params.vehicleDataId,
      data: docData
    });
  }

  public storeTtn(
    docData: ExcelGeneratorData,
    params: {
      requestId: number;
      orderNumber: string;
      vehicleDataId: string;
    }
  ): Observable<IDBValidKey> {
    return this.storeDoc({
      forwardingRequestId: params.requestId,
      docType: DocumentsForUploadRowType.TTN,
      orderNumber: params.orderNumber,
      vehicleDataId: params.vehicleDataId,
      data: docData
    });
  }

  public storeDoc(
    params: Omit<DocumentsForUploadRowData, 'user' | 'updatedOn'>
  ): Observable<IDBValidKey> {
    // Пользователя и метку времени фиксируем в момент ВЫЗОВА (как прежде), чтобы очередь сериализации не
    // сдвигала их на момент исполнения слота; под сериализацией должны быть только чтение и запись.
    const row: DocumentsForUploadRowData = {
      user: this.fetchCurrentUser(),
      updatedOn: Date.now(),
      ...params
    };
    return this.runSerial(() => this.storeDocAtomic(row));
  }

  /**
   * Плановая уборка очереди (под сериализацией): удаляет отправленные документы старше 30 дней
   * ({@link #deleteStaleRecords}) и отправленные документы заявок, которых больше нет на сервере
   * ({@link #deleteRecordsForMissedServerBackups}). Неотправленные не трогаются ни в одном случае.
   * Возвращает суммарное число удалённых записей.
   */
  public deleteOutdatedDocs(serverBackupIds: number[]): Observable<number> {
    return concat(
      this.runSerial(() => this.deleteStaleRecords()),
      this.runSerial(() => this.deleteRecordsForMissedServerBackups(serverBackupIds))
    ).pipe(reduce((acc, value) => acc + value, 0));
  }

  /**
   * Сохранение документа под сериализацией: читаем текущую запись и пишем в одном непрерывном слоте
   * цепочки, поэтому правка между чтением и записью невозможна. Отметку об отправке (uploadedChecksum)
   * с прежней записи переносим ТОЛЬКО если данные не изменились — иначе документ отредактирован после
   * выгрузки и должен считаться неотправленным (переотправится), а не потеряться под видом отправленного.
   */
  private async storeDocAtomic(
    row: DocumentsForUploadRowData
  ): Promise<IDBValidKey> {
    if (row.uploadedChecksum) {
      return this.markUploadedIfUnchanged(row);
    }
    const existingRow = await firstValueFrom(
      this.dbService.getByKey(this.storeName, this.rowKey(row))
    );
    if (existingRow) {
      const existing = existingRow as DocumentsForUploadRowData;
      if (
        existing.uploadedChecksum &&
        existing.uploadedChecksum === calculateDocumentChecksum(row.data)
      ) {
        row.uploadedChecksum = existing.uploadedChecksum;
      }
    }
    const savedKey = await firstValueFrom(
      this.dbService.bulkPut(this.storeName, [row])
    );
    return savedKey as IDBValidKey;
  }

  /**
   * Фиксирует отметку об успешной отправке (uploadedChecksum) на ТЕКУЩЕЙ записи, не затирая её данные.
   * Вызывается только из storeDocAtomic, то есть уже внутри слота сериализации, поэтому чтение и запись
   * атомарны. Отправка асинхронна: пока документ грузился, пользователь мог сохранить новую правку —
   * поэтому отметку ставим ТОЛЬКО если данные в хранилище всё ещё совпадают с реально выгруженными
   * (контрольная сумма сходится). Если изменились — правку не трогаем и отправленной не помечаем: она
   * останется в очереди и будет переотправлена.
   */
  private async markUploadedIfUnchanged(
    uploaded: DocumentsForUploadRowData
  ): Promise<IDBValidKey> {
    const existingRow = await firstValueFrom(
      this.dbService.getByKey(this.storeName, this.rowKey(uploaded))
    );
    const existing = existingRow as DocumentsForUploadRowData | undefined;
    if (!existing) {
      // Записи ещё нет (маловероятно) — сохраняем выгруженную как есть.
      const savedKey = await firstValueFrom(
        this.dbService.bulkPut(this.storeName, [uploaded])
      );
      return savedKey as IDBValidKey;
    }
    if (
      calculateDocumentChecksum(existing.data) === uploaded.uploadedChecksum
    ) {
      existing.uploadedChecksum = uploaded.uploadedChecksum;
      const savedKey = await firstValueFrom(
        this.dbService.bulkPut(this.storeName, [existing])
      );
      return savedKey as IDBValidKey;
    }
    // Данные изменились во время отправки — не затираем и отметку не ставим.
    return this.rowKey(existing);
  }

  private async deleteStaleRecords(): Promise<number> {
    const tooOldTs: number = Date.now() - TIMESTAMP_30_DAYS;
    const currentUser: string = this.fetchCurrentUser();
    const rows = await firstValueFrom(
      this.dbService.getAllByIndex<DocumentsForUploadRowData>(
        this.storeName,
        'updatedOn',
        IDBKeyRange.upperBound(tooOldTs)
      )
    );
    // Удаляем ТОЛЬКО успешно отправленные документы (есть uploadedChecksum) текущего пользователя.
    // Неотправленные (в очереди на загрузку) НЕ удаляем даже по истечении срока: иначе потеряли бы
    // документ, который так и не ушёл на сервер из-за долгого офлайна.
    const keys = rows
      .filter(row => row.user === currentUser && !!row.uploadedChecksum)
      .map(row => this.rowKey(row));
    if (keys.length === 0) {
      return 0;
    }
    await firstValueFrom(this.dbService.bulkDelete(this.storeName, keys));
    return keys.length;
  }

  private async deleteRecordsForMissedServerBackups(
    serverBackupIds: number[]
  ): Promise<number> {
    // Подстраховка от массового удаления: пустой серверный список сделал бы «нет в списке» истинным
    // для всех записей. Вызывающий (db-cleanup) это уже проверяет, но метод защищаем и сам.
    if (serverBackupIds.length === 0) {
      return 0;
    }
    const currentUser: string = this.fetchCurrentUser();
    const rows = await firstValueFrom(
      this.dbService.getAllByIndex<DocumentsForUploadRowData>(
        this.storeName,
        'user',
        IDBKeyRange.only(currentUser)
      )
    );
    // Удаляем ТОЛЬКО отправленные документы заявок, которых больше нет на сервере. Неотправленные
    // (без uploadedChecksum) НИКОГДА не трогаем: заявки может не быть в серверном списке именно потому,
    // что документ ещё не выгружен (первая отправка) — удаление означало бы потерю.
    const keys = rows
      .filter(
        row =>
          !!row.uploadedChecksum &&
          !serverBackupIds.includes(row.forwardingRequestId)
      )
      .map(row => this.rowKey(row));
    if (keys.length === 0) {
      return 0;
    }
    await firstValueFrom(this.dbService.bulkDelete(this.storeName, keys));
    return keys.length;
  }

  /**
   * Ставит операцию в очередь сериализации хранилища документов и возвращает её результат как
   * Observable. defer гарантирует планирование на подписке (холодный Observable, как прежде). Отклонение
   * одной операции не разрывает цепочку последующих.
   */
  private runSerial<T>(op: () => Promise<T>): Observable<T> {
    return defer(() => {
      const result = this.mutationChain.then(op, op);
      this.mutationChain = result.then(
        () => undefined,
        () => undefined
      );
      return from(result);
    });
  }

  /** Составной ключ записи в порядке keyPath хранилища DocumentsForUpload. */
  private rowKey(row: DocumentsForUploadRowData): IDBValidKey {
    return [
      row.user,
      row.forwardingRequestId,
      row.docType,
      row.orderNumber,
      row.vehicleDataId
    ];
  }

  private fetchCurrentUser(): string {
    const user = localStorage.getItem('login');
    if (!user) {
      throw new Error('Пользователь не авторизован!');
    }
    return user;
  }
}
